import { IdcardOutlined, ScanOutlined } from '@ant-design/icons';
import { Button, Select, Space, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { describeReader } from '../domain/identityReaderSelection';
import { useIdentityReaderSelection } from '../hooks/useIdentityReaderSelection';

/**
 * The two ways to read a customer's document, offered side by side on the client form.
 *
 * Both buttons are always rendered, including on a workstation where one of them cannot be used. A
 * disabled button with a reason attached tells an operator that the feature exists and what is
 * missing; a hidden one tells them nothing, and support gets a call asking where the scanner button
 * went.
 */
export interface IdentityReaderLauncherProps {
    /** Whether the form holding this launcher is on screen. Nothing is probed while it is not. */
    active: boolean;
    /** Opens the chip reader. */
    onChip: () => void;
    /**
     * Opens the optical reader on a resolved device.
     *
     * The workstation identity travels with it because this component is the one that probed the
     * service; the page would otherwise have to probe a second time to find out who answered.
     */
    onOptical: (deviceId: string, installationId: string | null) => void;
}

export const IdentityReaderLauncher = ({
    active,
    onChip,
    onOptical,
}: IdentityReaderLauncherProps) => {
    const { t } = useTranslation();
    const {
        status,
        chip,
        optical,
        device,
        chipDevice,
        middlewareUncertain,
        busy,
        choose,
        chooseChip,
        remember,
    } = useIdentityReaderSelection({ enabled: active });

    const installationId =
        status?.state === 'ready' ? (status.health.installationId ?? null) : null;

    const chipReason = chip.available ? '' : t(chip.messageKey);
    const opticalReason = optical.available ? '' : t(optical.messageKey);
    const mustChoose = optical.available && device.deviceId === null;
    const chooserVisible = device.readers.length > 1 || device.preferenceStale;
    // A card read names no device, so on a counter with two readers the service is the one that has
    // to be told which. Until it has been, the button is off for the same reason the scan button is:
    // an action that cannot succeed should not look as though it can.
    const chipMustChoose = chip.available && chipDevice.deviceId === null;
    const chipChooserVisible = chipDevice.readers.length > 1 || chipDevice.preferenceStale;
    // A warning, never a refusal. The workstation could not verify that the national middleware is
    // running, and an unverified prerequisite must not take an otherwise working action away.
    const middlewareWarning =
        middlewareUncertain && chip.available ? t('identityReader.chip.middlewareUnknown') : '';

    return (
        <Space wrap size="middle" align="center">
            <Tooltip title={chipMustChoose ? t('identityReader.chip.chooseFirst') : chipReason}>
                <Button
                    icon={<IdcardOutlined />}
                    disabled={!chip.available || chipDevice.deviceId === null}
                    onClick={onChip}
                    aria-describedby={chip.available ? undefined : 'identity-reader-chip-reason'}
                >
                    {t('clients.readIdCard')}
                </Button>
            </Tooltip>

            {chipChooserVisible && (
                <Select
                    aria-label={t('identityReader.chip.chooseLabel')}
                    style={{ minWidth: 220 }}
                    loading={busy}
                    value={chipDevice.deviceId ?? undefined}
                    placeholder={t('identityReader.chip.choosePlaceholder')}
                    onChange={deviceId => void chooseChip(deviceId)}
                    options={chipDevice.readers.map(reader => ({
                        value: reader.id,
                        label: describeReader(reader),
                        // A reader that is not attached is shown rather than dropped, so an
                        // operator can see that the one they used last week is the one missing.
                        disabled: !reader.connected,
                    }))}
                />
            )}

            <Tooltip title={mustChoose ? t('identityReader.optical.chooseFirst') : opticalReason}>
                <Button
                    icon={<ScanOutlined />}
                    disabled={!optical.available || device.deviceId === null}
                    onClick={() =>
                        device.deviceId !== null && onOptical(device.deviceId, installationId)
                    }
                >
                    {t('identityReader.optical.action')}
                </Button>
            </Tooltip>

            {chooserVisible && (
                <Space size="small" align="center">
                    <Select
                        aria-label={t('identityReader.optical.chooseLabel')}
                        style={{ minWidth: 220 }}
                        value={device.deviceId ?? undefined}
                        placeholder={t('identityReader.optical.choosePlaceholder')}
                        onChange={choose}
                        options={device.readers.map(reader => ({
                            value: reader.id,
                            label: describeReader(reader),
                            // A reader that is not attached is shown rather than dropped, so an
                            // operator can see that the one they used last week is the one missing.
                            disabled: !reader.connected,
                        }))}
                    />
                    <Button
                        size="small"
                        loading={busy}
                        disabled={device.deviceId === null}
                        onClick={() => void remember(device.deviceId)}
                    >
                        {t('identityReader.optical.remember')}
                    </Button>
                </Space>
            )}

            {/* Announced politely: this text changes underneath an operator while they work, and a
                scanner being unplugged should reach a screen reader without stealing focus. An
                `output` element is a status region by definition, so no role is declared. */}
            <output id="identity-reader-chip-reason">
                <Typography.Text
                    type={
                        optical.available && chip.available && !middlewareUncertain
                            ? 'secondary'
                            : 'warning'
                    }
                >
                    {statusLine([
                        chipReason,
                        middlewareWarning,
                        opticalReason,
                        chipDevice.preferenceStale ? t('identityReader.chip.preferenceStale') : '',
                        device.preferenceStale ? t('identityReader.optical.preferenceStale') : '',
                    ])}
                </Typography.Text>
            </output>
        </Space>
    );
};

/** The one sentence under the buttons: what is wrong, if anything is. */
function statusLine(parts: readonly string[]): string {
    return parts.filter(part => part.length > 0).join(' ');
}

export default IdentityReaderLauncher;
